/**
 * Einheiten stehen nicht mehr ineinander (GAME_DESIGN Abschnitt 12).
 *
 * Bis zum 13.10.2026 gab es gar keine Kollision: Zehn Creeps konnten
 * deckungsgleich auf demselben Punkt stehen, und im Nahkampf taten sie das
 * auch. Robins Vorgabe: **blockieren** statt wegschieben, und nur so viel
 * Abstand, dass man zwei Figuren als zwei erkennt.
 *
 * ZWEI DINGE PASSIEREN HIER, UND SIE SIND NICHT DASSELBE:
 *
 *   1. BLOCKIEREN. Wuerde ein Schritt in einen anderen hineinfuehren, wird
 *      genau dieser Teil der Bewegung weggenommen. Der Creep kommt nicht
 *      durch - er rutscht aber an seinem Hindernis entlang, statt stehen zu
 *      bleiben. Ohne dieses Entlangrutschen verklemmt sich eine Lane: Wer
 *      genau hinter einem Stehenden steht, kaeme nie mehr vorbei.
 *
 *   2. ENTFLECHTEN. Wer schon ineinander steht - frisch erschienen,
 *      beschworen, oder weil ein Dritter ihn hineingedraengt hat -, rutscht
 *      langsam heraus. Blockieren allein wuerde das nie aufloesen: Es
 *      verhindert nur, dass es schlimmer wird.
 *
 * WAS HIER BEWUSST NICHT PASSIERT: eine vollstaendige Aufloesung. Wer gegen
 * drei Nachbarn gleichzeitig gedrueckt wird, steht hinterher vielleicht noch
 * in einem davon. "Die Kollision muss nicht perfekt alles abdecken" (Robin,
 * 13.10.2026) - sie muss verhindern, dass Figuren verschmelzen. Eine exakte
 * Loesung braeuchte mehrere Durchlaeufe je Takt und wuerde auf dem Handy
 * bezahlt, ohne dass man den Unterschied saehe.
 *
 * DER HELD WIRD GEBREMST, ABER NICHT GESTOPPT. Auch ihn blockiert diese
 * Regel (der Aufrufer gibt ihm die Creeps als Hindernisse mit) - seit dem
 * 25.10.2026 gibt der Creep dabei aber langsam zur Seite, siehe
 * beiseiteGedraengt weiter unten. Vorher kam niemand durch eine Reihe
 * hindurch, und der Masterprompt (Abschnitt 33) verbietet ausdruecklich,
 * dass eine Reihe Creeps den Hero dauerhaft bodyblockt.
 *
 * Gerechnet wird ohne Zufall. Zwei exakt deckungsgleiche Einheiten weichen
 * in eine festgelegte Richtung aus - sonst liefen Server und Massentest
 * auseinander.
 */

import type { Vec2 } from '@dueline/shared';

/** Eine Einheit, die im Weg stehen kann. */
export interface Hindernis {
  x: number;
  y: number;
}

export interface BewegungEingabe {
  /** Wo die Einheit vor diesem Takt stand. */
  von: Vec2;
  /** Wohin sie ohne Hindernisse gehen wuerde. */
  nach: Vec2;
  /**
   * Was im Weg steht - OHNE die Einheit selbst.
   *
   * Der Aufrufer waehlt aus; diese Regel prueft nicht, wer dazugehoert.
   */
  hindernisse: readonly Hindernis[];
  /** Wie nah zwei Mitten einander kommen duerfen. */
  mindestabstand: number;
  /**
   * Wie weit eine Einheit je Takt ZUSAETZLICH herausrutschen darf, wenn sie
   * schon ineinander steht (Tempo mal Taktdauer).
   *
   * Ohne diese Zugabe wird nur verhindert, nie geloest.
   */
  entflechtung: number;
}

/**
 * Wohin die Einheit in diesem Takt wirklich kommt.
 *
 * Die Hindernisse werden der Reihe nach abgearbeitet. Das Ergebnis haengt
 * damit von ihrer Reihenfolge ab - das ist hingenommen und sogar nuetzlich:
 * Der naechstgelegene zuerst wirkt am staerksten, und der Aufrufer kann die
 * Reihenfolge stabil halten.
 */
export function blockierteBewegung(eingabe: BewegungEingabe): Vec2 {
  const mindestabstand = Math.max(0, eingabe.mindestabstand);
  if (mindestabstand === 0 || eingabe.hindernisse.length === 0) return eingabe.nach;

  let ziel: Vec2 = { x: eingabe.nach.x, y: eingabe.nach.y };

  for (const hindernis of eingabe.hindernisse) {
    let dx = ziel.x - hindernis.x;
    let dy = ziel.y - hindernis.y;
    let abstand = Math.hypot(dx, dy);

    if (abstand >= mindestabstand) continue;

    if (abstand < 1e-6) {
      /*
       * Exakt deckungsgleich - dann gibt es keine Richtung, in die es
       * hinausginge. Ausgewichen wird quer zur eigenen Laufrichtung; wer
       * steht, weicht nach rechts aus. Festgelegt und ohne Zufall, damit
       * Server und Massentest dasselbe rechnen.
       */
      const laufX = eingabe.nach.x - eingabe.von.x;
      const laufY = eingabe.nach.y - eingabe.von.y;
      const laenge = Math.hypot(laufX, laufY);

      if (laenge < 1e-6) {
        dx = 0;
        dy = 1;
      } else {
        dx = -laufY / laenge;
        dy = laufX / laenge;
      }
      abstand = 1;
    }

    const faktor = mindestabstand / abstand;
    ziel = { x: hindernis.x + dx * faktor, y: hindernis.y + dy * faktor };
  }

  /*
   * Und jetzt die Bremse auf die Korrektur.
   *
   * Erlaubt ist hoechstens: der eigene Schritt (den darf man komplett
   * zuruecknehmen - das IST das Blockieren) plus die kleine Zugabe zum
   * Entflechten. Ohne diese Grenze wuerde eine Einheit, die tief in einer
   * anderen steckt, in einem einzigen Takt herausgeschleudert.
   */
  const korrekturX = ziel.x - eingabe.nach.x;
  const korrekturY = ziel.y - eingabe.nach.y;
  const korrektur = Math.hypot(korrekturX, korrekturY);
  if (korrektur < 1e-9) return eingabe.nach;

  const schritt = Math.hypot(eingabe.nach.x - eingabe.von.x, eingabe.nach.y - eingabe.von.y);
  const erlaubt = schritt + Math.max(0, eingabe.entflechtung);
  if (korrektur <= erlaubt) return ziel;

  const anteil = erlaubt / korrektur;
  return {
    x: eingabe.nach.x + korrekturX * anteil,
    y: eingabe.nach.y + korrekturY * anteil,
  };
}

/*
 * HIER STAND "gedraengeFaktor" (13.10.2026, einen halben Tag lang).
 *
 * Er machte den Hero im Gedraenge langsamer, statt ihn aufzuhalten. Im
 * Spiel fuehlte sich das nicht richtig an - Robin: "mach eher, dass er
 * auch hart blockiert wird". Seitdem gilt fuer ihn dieselbe Regel wie
 * fuer Creeps: blockierteBewegung, aufgerufen aus applyHeroMovement.
 *
 * Die Bremse ist geloescht und nicht aufgehoben: Eine Regel, die niemand
 * mehr aufruft, sieht beim naechsten Lesen aus wie eine, die man benutzen
 * sollte.
 */

/** Wer draengt, und wohin. */
export interface DraengenEingabe {
  /** Wohin der Hero in diesem Takt wollte - OHNE Hindernisse gerechnet. */
  wunsch: Vec2;
  /** Seine Laufrichtung, Laenge 1. */
  richtung: Vec2;
  /** Wo der Creep jetzt steht. */
  creep: Vec2;
  /** Wie nah der Hero ihm kommen darf - derselbe Wert wie beim Blockieren. */
  mindestabstand: number;
  /** Wie weit der Creep in DIESEM Takt zur Seite gibt (Tempo mal Taktdauer). */
  schub: number;
}

/**
 * Wohin ein Creep ausweicht, in den der Hero hineinlaeuft
 * (Produktentscheidung vom 25.10.2026).
 *
 * Gedraengt wird nur, wer dem Hero WIRKLICH im Weg stand: Der Pruefpunkt
 * ist nicht seine heutige Stelle, sondern die, zu der er wollte. Damit
 * braucht es keine zweite Zone und kein Epsilon - genau die Creeps, die
 * seinen Schritt verhindert haben, geben nach.
 *
 * Ausgewichen wird QUER zur Laufrichtung und zu der Seite, auf der der
 * Creep schon liegt. Nicht nach vorn: Sonst schoebe der Hero eine ganze
 * Welle vor sich her, und ein Nahkaempfer koennte Creeps vom Portal
 * wegtreiben. Wer genau auf der Linie steht, weicht in eine festgelegte
 * Richtung aus - ohne Zufall, damit Server und Massentest dasselbe rechnen.
 *
 * Der Creep selbst laeuft danach wieder auf sein Ziel zu. Das Draengen
 * loest die Reihe also nicht auf, es macht nur einen Weg hindurch frei,
 * solange der Hero sich hineinlehnt.
 */
export function beiseiteGedraengt(eingabe: DraengenEingabe): Vec2 {
  const mindestabstand = Math.max(0, eingabe.mindestabstand);
  const schub = Math.max(0, eingabe.schub);
  if (mindestabstand === 0 || schub === 0) return eingabe.creep;

  const laenge = Math.hypot(eingabe.richtung.x, eingabe.richtung.y);
  // Wer steht, draengt niemanden.
  if (laenge < 1e-6) return eingabe.creep;

  const dx = eingabe.creep.x - eingabe.wunsch.x;
  const dy = eingabe.creep.y - eingabe.wunsch.y;
  // Er stand nicht im Weg.
  if (Math.hypot(dx, dy) >= mindestabstand) return eingabe.creep;

  const richtungX = eingabe.richtung.x / laenge;
  const richtungY = eingabe.richtung.y / laenge;

  /*
   * Auf welcher Seite der Laufspur liegt er? Das Kreuzprodukt sagt es.
   * Genau auf der Linie (0) zaehlt als eine Seite - festgelegt, nicht
   * zufaellig.
   */
  const quer = richtungX * dy - richtungY * dx;
  const seite = quer >= 0 ? 1 : -1;

  return {
    x: eingabe.creep.x + -richtungY * seite * schub,
    y: eingabe.creep.y + richtungX * seite * schub,
  };
}
